import React, { useState, useEffect } from 'react'
import { Popover, IconButton } from '@mui/material'
import { ColorizeRounded } from '@mui/icons-material'
import { SketchPicker } from 'react-color'
import { TextField } from '../text-field'
import { Text } from '../text'
import { color } from '../../theme'

const DEFAULT_PRESET_COLORS = ['#1B59F8', '#1FE08F', '#FFCE51', '#FF7324', '#FF3E13', '#551BFA']

interface CompactColorPickerProps {
  value: string | null | undefined
  onChange: (hex: string) => void
  presetColors?: string[]
  required?: boolean
  error?: boolean
  helperText?: string
  onBlur?: () => void
}

export const CompactColorPicker: React.FC<CompactColorPickerProps> = ({
  value,
  onChange,
  presetColors = DEFAULT_PRESET_COLORS,
  required,
  error,
  helperText = '',
  onBlur,
}) => {
  const [localColor, setLocalColor] = useState<any>(value)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setLocalColor(value)
  }, [value])

  const handleSwatchClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!localColor) {
      setLocalColor('#ffffff')
    }
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handlePickerChange = (color: any) => {
    setLocalColor(color?.hex)
  }

  const handlePickerChangeComplete = (color: any) => {
    onChange(color?.hex)
  }

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    setLocalColor(input)
    onChange(input)
  }

  const handleEyeDropper = async () => {
    if (!('EyeDropper' in window)) return
    try {
      const dropper = new (window as any).EyeDropper()
      const result = await dropper.open()
      setLocalColor(result.sRGBHex)
      onChange(result.sRGBHex)
    } catch {
      // user cancelled
    }
  }

  const eyeDropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          onClick={handleSwatchClick}
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: localColor,
            cursor: 'pointer',
            border: '1px solid rgba(0,0,0,0.12)',
            flexShrink: 0,
          }}
        />
        <TextField
          value={localColor}
          onChange={handleHexInput}
          placeholder="#561ecb"
          onBlur={onBlur}
          slotProps={{ htmlInput: { maxLength: 7 } }}
          style={{ width: 120 }}
          size="small"
          required={required}
          error={error}
        />
        {eyeDropperSupported && (
          <IconButton onClick={handleEyeDropper} size="small">
            <ColorizeRounded fontSize="small" />
          </IconButton>
        )}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { borderRadius: 2 } } }}
        >
          <div style={{ padding: 8 }}>
            <SketchPicker
              color={localColor}
              onChange={handlePickerChange}
              onChangeComplete={handlePickerChangeComplete}
              disableAlpha
              presetColors={presetColors}
              styles={{ default: { picker: { boxShadow: 'none', border: 'none' } } }}
            />
          </div>
        </Popover>
      </div>
      <Text style={{ marginTop: 4, color: color.red }} text={helperText} variant="body2" />
    </div>
  )
}
